import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { 
  MessageCircle, 
  Send, 
  Settings, 
  Clock, 
  CheckCircle2, 
  AlertTriangle, 
  Loader2, 
  Save,
  Play,
  History,
  Zap,
  Phone,
  Shield,
  Eye,
  EyeOff,
  User,
  Bell,
  RefreshCw,
  Sparkles,
  X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.07 } }
};

const item = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } }
};

const tabs = [
  { id: 'overview', label: 'Centro', icon: Bell },
  { id: 'settings', label: 'Configuración', icon: Settings },
  { id: 'history', label: 'Historial', icon: History },
];

export default function Notifications() {
  const [activeTab, setActiveTab] = useState('overview');
  const [settings, setSettings] = useState({
    whatsapp_enabled: false,
    twilio_account_sid: '',
    twilio_auth_token_masked: '',
    twilio_phone_number: '',
    alert_days: [7, 3, 1],
    message_template: ''
  });
  const [newToken, setNewToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [settingsRes, historyRes] = await Promise.all([
        axios.get(`${API}/api/notifications/settings`, { headers }),
        axios.get(`${API}/api/notifications/history`, { headers }),
      ]);
      setSettings(settingsRes.data);
      setHistory(historyRes.data);
    } catch (err) {
      console.error('Error fetching notifications data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const payload = {
        whatsapp_enabled: settings.whatsapp_enabled,
        twilio_account_sid: settings.twilio_account_sid,
        twilio_phone_number: settings.twilio_phone_number,
        alert_days: settings.alert_days,
        message_template: settings.message_template
      };
      if (newToken) payload.twilio_auth_token = newToken;
      
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/api/notifications/settings`, payload, { headers });
      toast.success('Configuración guardada');
      setNewToken('');
      fetchData();
    } catch (err) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.post(`${API}/api/notifications/send-test`, {}, { headers });
      toast.success(`Notificación de prueba ${data.status === 'simulated' ? 'simulada' : 'enviada'}`);
      fetchData();
    } catch (err) {
      toast.error('Error al enviar prueba');
    } finally {
      setSendingTest(false);
    }
  };

  const handleScanExpiring = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.post(`${API}/api/notifications/check-expiring`, {}, { headers });
      setScanResult(data);
      toast.success(`${data.count} notificaciones creadas`);
      fetchData();
    } catch (err) {
      toast.error('Error al escanear');
    } finally {
      setScanning(false);
    }
  };

  const toggleAlertDay = (day) => {
    if (settings.alert_days.includes(day)) {
      setSettings({ ...settings, alert_days: settings.alert_days.filter(d => d !== day) });
    } else {
      setSettings({ ...settings, alert_days: [...settings.alert_days, day].sort((a, b) => b - a) });
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      simulated: { label: 'Simulado', color: '#6366f1', bg: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' },
      sent: { label: 'Enviado', color: '#22c55e', bg: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
      failed: { label: 'Fallido', color: '#ef4444', bg: 'bg-red-500/10 text-red-400 border-red-500/20' },
    };
    return config[status] || config.simulated;
  };

  const isConfigured = settings.twilio_account_sid && settings.twilio_auth_token_masked;

  const recentSimulated = history.filter(n => n.status === 'simulated').length;
  const recentSent = history.filter(n => n.status === 'sent').length;
  const todayCount = history.filter(n => {
    const today = new Date().toISOString().split('T')[0];
    return n.created_at?.startsWith(today);
  }).length;

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="h-10 w-56 nova-skeleton" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[...Array(3)].map((_, i) => <div key={i} className="h-32 nova-skeleton rounded-xl" />)}
          </div>
          <div className="h-96 nova-skeleton rounded-xl" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div 
        data-testid="notifications-page" 
        className="space-y-8"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {/* Header */}
        <motion.div variants={item} className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-headline text-white">Notificaciones</h1>
            <p className="text-caption mt-1">WhatsApp automático para membresías por vencer</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleScanExpiring}
              disabled={scanning}
              data-testid="scan-expiring-button"
              className="nova-btn nova-btn-secondary"
            >
              {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Escanear vencimientos
            </button>
            <button
              onClick={handleSendTest}
              disabled={sendingTest}
              data-testid="send-test-button"
              className="nova-btn nova-btn-primary"
            >
              {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Enviar prueba
            </button>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div variants={container} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div variants={item} className="nova-card p-5 bg-gradient-to-br from-emerald-500/8 to-transparent">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-emerald-400" />
              </div>
              <span className="text-caption">Hoy</span>
            </div>
            <p className="text-2xl font-extrabold text-white tabular-nums">{todayCount}</p>
            <p className="text-xs text-[#48484d] mt-0.5">Notificaciones</p>
          </motion.div>

          <motion.div variants={item} className="nova-card p-5 bg-gradient-to-br from-indigo-500/8 to-transparent">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-lg bg-indigo-500/15 flex items-center justify-center">
                <Send className="w-4 h-4 text-indigo-400" />
              </div>
              <span className="text-caption">Enviados</span>
            </div>
            <p className="text-2xl font-extrabold text-white tabular-nums">{recentSent}</p>
            <p className="text-xs text-[#48484d] mt-0.5">WhatsApp real</p>
          </motion.div>

          <motion.div variants={item} className="nova-card p-5 bg-gradient-to-br from-purple-500/8 to-transparent">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-lg bg-purple-500/15 flex items-center justify-center">
                <Zap className="w-4 h-4 text-purple-400" />
              </div>
              <span className="text-caption">Simulados</span>
            </div>
            <p className="text-2xl font-extrabold text-white tabular-nums">{recentSimulated}</p>
            <p className="text-xs text-[#48484d] mt-0.5">Sin credenciales</p>
          </motion.div>

          <motion.div variants={item} className="nova-card p-5 bg-gradient-to-br from-amber-500/8 to-transparent">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center">
                <Shield className="w-4 h-4 text-amber-400" />
              </div>
              <span className="text-caption">Estado</span>
            </div>
            <p className={`text-sm font-bold ${isConfigured ? 'text-emerald-400' : 'text-amber-400'}`}>
              {isConfigured ? 'Conectado' : 'Sin configurar'}
            </p>
            <p className="text-xs text-[#48484d] mt-0.5">WhatsApp API</p>
          </motion.div>
        </motion.div>

        {/* Scan Result */}
        <AnimatePresence>
          {scanResult && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="nova-card p-5 border-emerald-500/20 bg-gradient-to-r from-emerald-500/5 to-transparent">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    <div>
                      <p className="text-sm font-semibold text-white">Escaneo completado</p>
                      <p className="text-xs text-[#6e6e73]">{scanResult.count} notificaciones creadas para clientes con membresía por vencer</p>
                    </div>
                  </div>
                  <button onClick={() => setScanResult(null)} className="p-1 hover:bg-white/5 rounded-lg transition-colors">
                    <X className="w-4 h-4 text-[#6e6e73]" />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tabs */}
        <motion.div variants={item}>
          <div className="flex bg-[var(--nova-surface)] border border-[var(--nova-border)] rounded-xl p-1 w-fit">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg transition-all ${
                  activeTab === tab.id 
                    ? 'bg-[var(--nova-primary)] text-white shadow-lg shadow-indigo-500/20' 
                    : 'text-[var(--nova-text-tertiary)] hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* How it works */}
              <div className="nova-card p-6 bg-gradient-to-br from-indigo-500/5 to-purple-500/3 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/5 rounded-full blur-[80px]" />
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl bg-indigo-500/15 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-indigo-400" />
                    </div>
                    <h3 className="text-title text-white">¿Cómo funciona?</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                      { step: '01', title: 'Configura', desc: 'Conecta tu cuenta de WhatsApp Business via Twilio en la pestaña de Configuración', icon: Settings },
                      { step: '02', title: 'Automatiza', desc: 'Nova detecta membresías por vencer y envía recordatorios automáticos por WhatsApp', icon: Zap },
                      { step: '03', title: 'Retiene', desc: 'Tus clientes reciben el mensaje y renuevan antes de que expire su membresía', icon: CheckCircle2 },
                    ].map((s, i) => (
                      <div key={i} className="flex gap-4">
                        <div className="shrink-0">
                          <span className="text-3xl font-extrabold text-white/[0.06]">{s.step}</span>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-white mb-1">{s.title}</h4>
                          <p className="text-xs text-[#6e6e73] leading-relaxed">{s.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Connection status */}
              <div className="nova-card p-6">
                <h3 className="text-title text-white mb-4">Estado de conexión</h3>
                <div className={`flex items-center gap-4 p-5 rounded-xl border ${
                  isConfigured 
                    ? 'border-emerald-500/20 bg-emerald-500/5' 
                    : 'border-amber-500/20 bg-amber-500/5'
                }`}>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    isConfigured ? 'bg-emerald-500/15' : 'bg-amber-500/15'
                  }`}>
                    <MessageCircle className={`w-6 h-6 ${isConfigured ? 'text-emerald-400' : 'text-amber-400'}`} />
                  </div>
                  <div className="flex-1">
                    <p className={`font-semibold ${isConfigured ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {isConfigured ? 'WhatsApp Business conectado' : 'WhatsApp Business no configurado'}
                    </p>
                    <p className="text-sm text-[#6e6e73] mt-0.5">
                      {isConfigured 
                        ? `Número: ${settings.twilio_phone_number || 'No definido'}`
                        : 'Configura tus credenciales de Twilio para enviar mensajes reales'
                      }
                    </p>
                  </div>
                  <button
                    onClick={() => setActiveTab('settings')}
                    className="nova-btn nova-btn-secondary text-sm"
                  >
                    {isConfigured ? 'Editar' : 'Configurar'}
                  </button>
                </div>

                {/* Simulation mode notice */}
                {!isConfigured && (
                  <div className="mt-4 flex items-start gap-3 p-4 rounded-xl bg-indigo-500/5 border border-indigo-500/15">
                    <AlertTriangle className="w-5 h-5 text-indigo-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-indigo-300">Modo simulación activo</p>
                      <p className="text-xs text-[#6e6e73] mt-1">Los mensajes se registran en el historial pero no se envían realmente. Configura Twilio para activar el envío real.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Recent notifications */}
              <div className="nova-card p-6">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-title text-white">Últimas notificaciones</h3>
                  <button onClick={() => setActiveTab('history')} className="text-xs font-medium text-indigo-400 hover:text-indigo-300 transition-colors">
                    Ver todo
                  </button>
                </div>
                {history.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-14 h-14 rounded-2xl bg-[var(--nova-surface-raised)] flex items-center justify-center mb-4">
                      <MessageCircle className="w-7 h-7 text-[var(--nova-text-quaternary)]" />
                    </div>
                    <p className="text-sm font-medium text-white mb-1">No hay notificaciones</p>
                    <p className="text-caption">Envía una prueba o escanea vencimientos</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {history.slice(0, 5).map((notif, i) => {
                      const badge = getStatusBadge(notif.status);
                      return (
                        <motion.div
                          key={notif.id || i}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.04 }}
                          className="flex items-center gap-4 p-4 rounded-xl bg-[var(--nova-bg-subtle)] border border-[var(--nova-border-subtle)] hover:border-[var(--nova-border)] transition-colors"
                        >
                          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                            <MessageCircle className="w-5 h-5 text-emerald-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-white truncate">{notif.client_name}</p>
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badge.bg}`}>
                                {badge.label}
                              </span>
                              {notif.type === 'test' && (
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20">
                                  Prueba
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-[#6e6e73] mt-1 truncate">{notif.message}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-xs text-[#48484d]">
                              {notif.created_at ? new Date(notif.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }) : ''}
                            </p>
                            <p className="text-[10px] text-[#2c2c2e]">
                              {notif.created_at ? new Date(notif.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : ''}
                            </p>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'settings' && (
            <motion.div
              key="settings"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              className="space-y-6 max-w-2xl"
            >
              {/* WhatsApp Toggle */}
              <div className="nova-card p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center">
                      <MessageCircle className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-title text-white">WhatsApp Business</h3>
                      <p className="text-xs text-[#6e6e73]">Envía recordatorios automáticos vía WhatsApp</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSettings({ ...settings, whatsapp_enabled: !settings.whatsapp_enabled })}
                    data-testid="whatsapp-toggle"
                    className={`relative w-12 h-7 rounded-full transition-colors ${
                      settings.whatsapp_enabled ? 'bg-emerald-500' : 'bg-[#2c2c2e]'
                    }`}
                  >
                    <div className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full transition-transform shadow-lg ${
                      settings.whatsapp_enabled ? 'translate-x-5' : 'translate-x-0'
                    }`} />
                  </button>
                </div>
              </div>

              {/* Twilio Credentials */}
              <div className="nova-card p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-9 h-9 rounded-lg bg-indigo-500/15 flex items-center justify-center">
                    <Shield className="w-4 h-4 text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-title text-white">Credenciales de Twilio</h3>
                    <p className="text-xs text-[#6e6e73]">
                      <a href="https://www.twilio.com/console" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                        Obtener en twilio.com/console
                      </a>
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Account SID</label>
                    <input
                      type="text"
                      value={settings.twilio_account_sid}
                      onChange={(e) => setSettings({ ...settings, twilio_account_sid: e.target.value })}
                      data-testid="twilio-sid-input"
                      className="nova-input font-mono text-sm"
                      placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Auth Token</label>
                    <div className="relative">
                      <input
                        type={showToken ? 'text' : 'password'}
                        value={newToken || settings.twilio_auth_token_masked}
                        onChange={(e) => setNewToken(e.target.value)}
                        data-testid="twilio-token-input"
                        className="nova-input font-mono text-sm pr-12"
                        placeholder={settings.twilio_auth_token_masked || 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'}
                      />
                      <button
                        onClick={() => setShowToken(!showToken)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#6e6e73] hover:text-white transition-colors"
                      >
                        {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Número WhatsApp</label>
                    <input
                      type="tel"
                      value={settings.twilio_phone_number}
                      onChange={(e) => setSettings({ ...settings, twilio_phone_number: e.target.value })}
                      data-testid="twilio-phone-input"
                      className="nova-input"
                      placeholder="+521234567890"
                    />
                  </div>
                </div>
              </div>

              {/* Alert Rules */}
              <div className="nova-card p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center">
                    <Bell className="w-4 h-4 text-amber-400" />
                  </div>
                  <h3 className="text-title text-white">Reglas de alerta</h3>
                </div>
                <p className="text-sm text-[#6e6e73] mb-4">Enviar notificación cuando falten estos días para el vencimiento:</p>
                <div className="flex flex-wrap gap-2">
                  {[14, 7, 5, 3, 2, 1].map((day) => (
                    <button
                      key={day}
                      onClick={() => toggleAlertDay(day)}
                      className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        settings.alert_days.includes(day)
                          ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/25'
                          : 'bg-[var(--nova-surface-raised)] text-[var(--nova-text-tertiary)] border border-[var(--nova-border)] hover:border-[var(--nova-border-strong)]'
                      }`}
                    >
                      {day === 1 ? '1 día' : `${day} días`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message Template */}
              <div className="nova-card p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-9 h-9 rounded-lg bg-purple-500/15 flex items-center justify-center">
                    <MessageCircle className="w-4 h-4 text-purple-400" />
                  </div>
                  <h3 className="text-title text-white">Plantilla de mensaje</h3>
                </div>
                <textarea
                  value={settings.message_template}
                  onChange={(e) => setSettings({ ...settings, message_template: e.target.value })}
                  data-testid="message-template-input"
                  className="nova-input h-28 resize-none text-sm"
                  placeholder="Hola {nombre}, tu membresía en {negocio} vence en {dias} días..."
                />
                <div className="flex flex-wrap gap-2 mt-3">
                  {['{nombre}', '{negocio}', '{dias}'].map((variable) => (
                    <span key={variable} className="text-xs px-2.5 py-1 rounded-lg bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 font-mono">
                      {variable}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-[#48484d] mt-2">Variables disponibles para personalizar el mensaje</p>

                {/* Preview */}
                {settings.message_template && (
                  <div className="mt-5 p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/15">
                    <p className="text-xs text-emerald-400 font-semibold mb-2 uppercase tracking-wider">Vista previa</p>
                    <p className="text-sm text-[#a1a1a6]">
                      {settings.message_template
                        .replace('{nombre}', 'Juan Pérez')
                        .replace('{negocio}', 'Mi Gimnasio')
                        .replace('{dias}', '3')}
                    </p>
                  </div>
                )}
              </div>

              {/* Save */}
              <div className="flex justify-end pb-4">
                <button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  data-testid="save-notification-settings"
                  className="nova-btn nova-btn-primary px-8"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Guardar configuración
                </button>
              </div>
            </motion.div>
          )}

          {activeTab === 'history' && (
            <motion.div
              key="history"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
            >
              <div className="nova-card overflow-hidden">
                {history.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <div className="w-14 h-14 rounded-2xl bg-[var(--nova-surface-raised)] flex items-center justify-center mb-4">
                      <History className="w-7 h-7 text-[var(--nova-text-quaternary)]" />
                    </div>
                    <p className="text-sm font-medium text-white mb-1">Sin historial</p>
                    <p className="text-caption">Las notificaciones enviadas aparecerán aquí</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[var(--nova-border-subtle)]">
                    <div className="grid grid-cols-12 gap-4 px-6 py-3 text-overline">
                      <div className="col-span-3">Cliente</div>
                      <div className="col-span-2">Teléfono</div>
                      <div className="col-span-3">Mensaje</div>
                      <div className="col-span-1">Canal</div>
                      <div className="col-span-1">Estado</div>
                      <div className="col-span-2 text-right">Fecha</div>
                    </div>
                    {history.map((notif, i) => {
                      const badge = getStatusBadge(notif.status);
                      return (
                        <motion.div
                          key={notif.id || i}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: i * 0.03 }}
                          className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-[var(--nova-surface)] transition-colors"
                        >
                          <div className="col-span-3 flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                              <User className="w-4 h-4 text-emerald-400" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-white truncate">{notif.client_name}</p>
                              {notif.type === 'test' && <p className="text-[10px] text-purple-400">Prueba</p>}
                            </div>
                          </div>
                          <div className="col-span-2">
                            <p className="text-sm text-[#6e6e73] flex items-center gap-1.5">
                              <Phone className="w-3.5 h-3.5" />
                              <span className="truncate">{notif.client_phone || '—'}</span>
                            </p>
                          </div>
                          <div className="col-span-3">
                            <p className="text-sm text-[#6e6e73] truncate">{notif.message}</p>
                          </div>
                          <div className="col-span-1">
                            <span className="text-xs font-medium text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-lg">
                              WhatsApp
                            </span>
                          </div>
                          <div className="col-span-1">
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badge.bg}`}>
                              {badge.label}
                            </span>
                          </div>
                          <div className="col-span-2 text-right">
                            <p className="text-xs text-[#6e6e73]">
                              {notif.created_at ? new Date(notif.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }) : ''}
                            </p>
                            <p className="text-[10px] text-[#48484d]">
                              {notif.created_at ? new Date(notif.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : ''}
                            </p>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </Layout>
  );
}
